import { useState, useCallback, useEffect, useMemo, lazy, Suspense } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
// Lazy — keep react-markdown + remark-gfm out of the initial bundle (matches DeckPage).
const DocViewer = lazy(() => import('@/components/DocViewer').then(m => ({ default: m.DocViewer })))
import { DeckHelmet } from '@/components/DeckHelmet'
import { deckLoaders, getDeckMeta } from '@/decks/registry'
import type { DeckDoc, DeckTheme } from '@/decks/registry'
import { useThemeColor } from '@/hooks/useThemeColor'

const WRAP: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: '100vh', fontFamily: 'Roboto, sans-serif',
}
const INNER: React.CSSProperties = { textAlign: 'center' }
const H1: React.CSSProperties = { fontSize: 24, marginBottom: 8 }
const P: React.CSSProperties = { color: '#666' }

function resolveTheme(slug: string | undefined): DeckTheme {
  const stored = slug ? localStorage.getItem(`gc-slides-theme:${slug}`) : null
  if (stored === 'dark' || stored === 'light') return stored
  return getDeckMeta(slug ?? '')?.defaultTheme ?? 'light'
}

// Standalone full-page doc reader on its own route (/deck/:slug/doc/:docId).
// This is the "Open in new tab" target — renders the doc as a page, not a
// modal popup over the deck. Loads the deck module directly for its docs.
export function DocPage() {
  const { slug, docId } = useParams<{ slug: string; docId: string }>()
  const navigate = useNavigate()
  const [docs, setDocs] = useState<DeckDoc[] | null>(null)
  const theme = useMemo(() => resolveTheme(slug), [slug])

  useEffect(() => {
    let cancelled = false
    const loader = slug ? deckLoaders[slug] : undefined
    if (!loader) { setDocs([]); return }
    loader().then(mod => { if (!cancelled) setDocs(mod.docs ?? []) })
    return () => { cancelled = true }
  }, [slug])

  useEffect(() => {
    document.body.setAttribute('data-theme', theme)
    return () => { document.body.removeAttribute('data-theme') }
  }, [theme])
  useThemeColor(theme === 'dark' ? '#111111' : '#FAFAFA')

  const selectDoc = useCallback((id: string) => {
    if (slug) navigate(`/deck/${slug}/doc/${id}`, { replace: true })
  }, [slug, navigate])

  const goBack = useCallback(() => {
    if (slug) navigate(`/deck/${slug}`)
  }, [slug, navigate])

  if (!slug || !deckLoaders[slug]) {
    return (
      <div style={WRAP}>
        <div style={INNER}>
          <h1 style={H1}>Deck not found</h1>
          <p style={P}>Available: {Object.keys(deckLoaders).map(s => `/deck/${s}`).join(', ')}</p>
        </div>
      </div>
    )
  }

  if (docs === null) return null // loading

  if (docs.length === 0) {
    return (
      <div style={WRAP}>
        <div style={INNER}>
          <h1 style={H1}>No docs</h1>
          <p style={P}>This deck has no attached documents.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <DeckHelmet slug={slug} />
      <Suspense fallback={null}>
        <DocViewer docs={docs} activeDocId={docId} onSelectDoc={selectDoc} onBack={goBack} variant="page" />
      </Suspense>
    </>
  )
}
